import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { callApi } from 'src/utils/apiCall';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { updateError } from './user';

const INITIAL_STATE = {
    leadOverview: [],
    revenueDistribution: [],
    revenueDistributionByTreatment: [],
    leadSources: [],
    referrals: [],
    statusMatrix: [],
    treatmentEnquiries: [],
    // Per-practice monthly lead counts (created_at basis) for the Practice
    // Performance line/trend view: [{ practiceId, months: [{ month, count }] }].
    monthlyLeadsByPractice: [],
    _allPracticesFetchKey: null,
    loading: false,
};

const STATUS_SET = {
    'NEW': "NEW",
    'CONSULTATION_BOOKED': "CONSULTATION_BOOKED",
    'CONSULTATION_ATTENDED': "CONSULTATION_ATTENDED",
    'TREATMENT_STARTED': "TREATMENT_STARTED",
    'CLOSED': "CLOSED"
};

const slice = createSlice({
    name: 'allPracticeReports',
    initialState: INITIAL_STATE,
    reducers: {
        setLeadOverviewTable: (state, action) => { state.leadOverview = action.payload; },
        setRevenueByCategory: (state, action) => { state.revenueDistribution = action.payload; },
        setRevenueByTreatmentName: (state, action) => { state.revenueDistributionByTreatment = action.payload; },
        setLeadSourceDist: (state, action) => { state.leadSources = action.payload; },
        setReferralDist: (state, action) => { state.referrals = action.payload; },
        setTreatmentStatusMatrix: (state, action) => { state.statusMatrix = action.payload; },
        setTreatmentEnquiryCount: (state, action) => { state.treatmentEnquiries = action.payload; },
        setMonthlyLeadsByPractice: (state, action) => { state.monthlyLeadsByPractice = action.payload; },
        setAllPracticesFetchKey: (state, action) => { state._allPracticesFetchKey = action.payload; },
        setAllPracticesLoading: (state, action) => { state.loading = action.payload; },
    },
});

export const {
    setLeadOverviewTable,
    setRevenueByCategory,
    setLeadSourceDist,
    setReferralDist,
    setTreatmentStatusMatrix,
    setTreatmentEnquiryCount,
    setRevenueByTreatmentName,
    setMonthlyLeadsByPractice,
    setAllPracticesFetchKey,
    setAllPracticesLoading,
} = slice.actions;

export function fetchPracticeAnalytics(practiceIds, dateRange) {
    return async (dispatch) => {
        const baseUrl = '/api/v1/practices';

        dispatch(setAllPracticesLoading(true));
        try {
            // PERF: collapse the per-practice fan-out. The /reports/leads/ endpoint
            // scopes cleanly by lead practice (`practice_id__in`) and supports
            // `group_by[practice]`, so every leads aggregate is fetched ONCE across
            // all practices in a single query and split client-side — turning ~10×N
            // round-trips into ~10. (Appointments + treatment metadata + practitioner
            // stats stay per-practice: the appointments report scopes by *assignee*
            // practice, not lead practice, so grouping it by lead practice would
            // mis-split; treatments/practice-user aren't reports-aggregate endpoints.)
            const ids = practiceIds.join(',');
            const repPrefix = `${baseUrl}/${practiceIds[0]}/reports`;
            const dr = `created_at__gte=${dateRange.start}&created_at__lte=${dateRange.end}`;
            const pin = `practice__in=${ids}`;
            // Matrix status columns + Practice Comparison funnel (Booked/Attended/Started/
            // Closed) — TRANSITION basis, per PracticeTreatment, computed the SAME way the
            // drilldown popup counts so a cell ALWAYS equals its popup.
            //
            // The /reports/leads/ aggregate can't scope a transition to the *grouped*
            // treatment: state_transition_params is lead-wide, so grouping by treatment
            // counts leads that merely HAVE the treatment AND had SOME transition — it
            // under/over-counts vs the popup (e.g. Composite Bonding / Started: aggregate 26
            // vs the verified truth 28). So instead we page the lead list per stage and
            // verify each treatment's OWN state_transition_logs client-side — identical to
            // TreatmentLeadPopup.hasTransitionInRange — then bucket by (practice, PracticeTreatment).
            const rangeStart = dayjs(dateRange.start);
            const rangeEnd = dayjs(dateRange.end);
            const hasTransitionInRange = (lt, targetState) => (lt.state_transition_logs || []).some((log) => {
                if ((log.to_state || log.state) !== targetState) return false;
                const ts = parseApiDate(log.timestamp);
                return ts?.isValid() && !ts.isBefore(rangeStart) && !ts.isAfter(rangeEnd);
            });
            const stageFields = 'fields={practice,lead_treatments{treatment_data,custom_price,state_transition_logs,treatment,status}}';
            const stageRows = (d) => {
                if (Array.isArray(d?.results)) return d.results;
                if (Array.isArray(d)) return d;
                return [];
            };
            // Page every lead that hit `status` in range, verify per-treatment, bucket by
            // (practice, PracticeTreatment). Returns a synthetic aggregate-shaped response so
            // indexByPractice() consumes it unchanged. One paged sweep per stage.
            const fetchVerifiedStage = async (status) => {
                const sp = encodeURIComponent(JSON.stringify({ state: status, timestamp_gte: dateRange.start, timestamp_lte: dateRange.end }));
                const base = `${baseUrl}/${practiceIds[0]}/leads/?state_transition_params=${sp}&${pin}&is_archived_included=true&${stageFields}&ordering=-created_at`;
                const PAGE_CAP = 5000;
                const firstRes = await callApi(`${base}&limit=100`);
                if (firstRes && firstRes.ok === false) {
                    console.warn(`[allPracticeReports] matrix ${status}: first page failed (status ${firstRes.status}) — counts may be under-reported.`);
                }
                let leads = stageRows(firstRes?.data);
                const total = firstRes?.data?.count ?? leads.length;
                const pageSize = leads.length || 100;
                const cap = Math.min(total, PAGE_CAP);
                // Visible signal when we stop short of the true total — a silently low
                // reporting number is worse than a warning. Long-term fix: backend #6046.
                if (total > PAGE_CAP) {
                    console.warn(`[allPracticeReports] matrix ${status}: ${total} leads exceeds the ${PAGE_CAP} page cap — cells for this stage are UNDER-COUNTED. Narrow the date range or fix backend #6046.`);
                }
                if (leads.length < cap) {
                    const offsets = [];
                    for (let off = pageSize; off < cap; off += pageSize) offsets.push(off);
                    const CONC = 6;
                    for (let i = 0; i < offsets.length; i += CONC) {
                        const batch = offsets.slice(i, i + CONC);
                        // eslint-disable-next-line no-await-in-loop
                        const pages = await Promise.all(batch.map((off) => callApi(`${base}&limit=${pageSize}&offset=${off}`)));
                        const failed = pages.filter((p) => p && p.ok === false).length;
                        if (failed) {
                            console.warn(`[allPracticeReports] matrix ${status}: ${failed}/${batch.length} page(s) failed — counts may be under-reported.`);
                        }
                        const batchRows = pages.flatMap((p) => stageRows(p?.data));
                        leads = leads.concat(batchRows);
                        if (leads.length >= cap) break;
                    }
                }
                // Count one per qualifying lead_treatment row — this MATCHES the drilldown
                // popup, which lists one row per transitioned treatment (verified against
                // live data: 22 distinct leads but 27 treatment rows → popup shows 27).
                // Do NOT dedupe by lead id here: that would under-count vs the popup.
                const buckets = new Map();
                leads.forEach((lead) => {
                    (lead.lead_treatments || []).forEach((lt) => {
                        const tid = lt.treatment_data?.id;
                        if (tid == null || !hasTransitionInRange(lt, status)) return;
                        const key = `${lead.practice}::${tid}`;
                        const cur = buckets.get(key) || { practice: lead.practice, tid, count_id: 0, sum: 0 };
                        cur.count_id += 1;
                        cur.sum += Number(lt.custom_price) || 0;
                        buckets.set(key, cur);
                    });
                });
                const rows = [...buckets.values()].map((b) => ({
                    practice: b.practice,
                    lead_treatments__treatment: b.tid,
                    count_id: b.count_id,
                    sum_lead_treatments__custom_price: b.sum,
                }));
                return { data: { data: rows } };
            };

            const [
                totalLeadsRes,
                totalTreatmentsRes,
                treatmentRevenueDistRes,
                treatmentRevenueByNameDistRes,
                leadSourceStatsRes,
                leadReferralStatsRes,
                treatmentEnquiryCountsRes,
                archivedLeadCountsRes,
                newEnquiryByTreatmentRes,
                cohortComparisonRes,
                perPracticeRes,
                matrixBookedRes,
                matrixAttendedRes,
                matrixStartedRes,
                matrixClosedRes,
                monthlyLeadsRes,
            ] = await Promise.all([
                callApi(`${repPrefix}/leads/?aggregate[Count]=id&group_by[practice]=true&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?aggregate[Count]=lead_treatments&group_by[practice]=true&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[practice]=true&group_by[lead_treatments__treatment__treatment_item__treatment_category]=true&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[practice]=true&group_by[lead_treatments__treatment]=true&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?group_by[practice]=true&group_by[lead_source__type]=true&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?group_by[practice]=true&group_by[lead_source__referral]=true&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?aggregate[Count]=id&group_by[practice]=true&group_by[lead_treatments__treatment__treatment_item__treatment_category]=true&lead_treatments__status=NEW&${pin}&${dr}`),
                callApi(`${repPrefix}/leads/?group_by[practice]=true&group_by[is_archived]=true&aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&${pin}&${dr}`),
                // (c) Matrix New Enquiries — created_at basis, per treatment (mirrors the New list page).
                callApi(`${repPrefix}/leads/?aggregate[Count]=id&aggregate[Sum]=lead_treatments__custom_price&group_by[practice]=true&group_by[lead_treatments__treatment]=true&lead_treatments__status=NEW&${pin}&${dr}`),
                // Practice Comparison uses a lead-created cohort and real
                // appointment outcomes. Keep this separate from the activity
                // matrix above: the two reports answer different questions.
                callApi(`${repPrefix}/lead-status-summary/?mode=practice_comparison_cohort&include_archived=true&${pin}&start=${dayjs(dateRange.start).format('YYYY-MM-DD')}&end=${dayjs(dateRange.end).format('YYYY-MM-DD')}`),
                // Per-practice endpoints (not collapsible — see note above). The
                // /reports/practice-user/ call that used to sit here was removed — it
                // only fed `practitionerSummary`, which had no consumer (dead code).
                Promise.all(practiceIds.map((pid) => Promise.all([
                    callApi(`${baseUrl}/${pid}/treatments/?enabled=true&ordering=treatment_item__name&fields={id,practice,price,colour,icon,treatment_item{id,name,treatment_category{id,name}}}`),
                    callApi(`${baseUrl}/${pid}/reports/appointments/?is_archived=false&group_by[status]=true&aggregate[Count]=id&datetime__gte=${dateRange.start}&datetime__lte=${dateRange.end}`),
                ]))),
                // (c) Matrix Booked/Attended/Started/Closed — TRANSITION basis, per PracticeTreatment,
                // counted the popup's way (paged list + per-treatment log verification) so
                // cell == popup. See fetchVerifiedStage above.
                fetchVerifiedStage('CONSULTATION_BOOKED'),
                fetchVerifiedStage('CONSULTATION_ATTENDED'),
                fetchVerifiedStage('TREATMENT_STARTED'),
                fetchVerifiedStage('CLOSED'),
                // Practice Performance line view — per-practice monthly lead counts
                // (created_at basis, within the selected range). Month = calendar month
                // number; the analytics presets stay within a single year.
                callApi(`${repPrefix}/leads/?aggregate[Count]=id&group_by[practice]=true&group_by[created_at__month]=true&${pin}&${dr}`),
            ]);

            // Split a group_by[practice] response into { [practiceId]: rows } with the practice key stripped.
            const indexByPractice = (res) => {
                const out = {};
                (res?.data?.data || []).forEach((row) => {
                    const pid = row.practice;
                    const rest = { ...row };
                    delete rest.practice;
                    (out[pid] = out[pid] || []).push(rest);
                });
                return out;
            };
            const scalarByPractice = (res, field) => {
                const out = {};
                (res?.data?.data || []).forEach((row) => { out[row.practice] = row[field]; });
                return out;
            };

            const totalLeadsMap = scalarByPractice(totalLeadsRes, 'count_id');
            const totalTreatmentsMap = scalarByPractice(totalTreatmentsRes, 'count_lead_treatments');
            const revenueDistMap = indexByPractice(treatmentRevenueDistRes);
            const revenueByNameMap = indexByPractice(treatmentRevenueByNameDistRes);
            const leadSourceMap = indexByPractice(leadSourceStatsRes);
            const leadReferralMap = indexByPractice(leadReferralStatsRes);
            const enquiryMap = indexByPractice(treatmentEnquiryCountsRes);
            const archivedMap = indexByPractice(archivedLeadCountsRes);
            const newEnquiryMap = indexByPractice(newEnquiryByTreatmentRes);
            const cohortComparisonMap = {};
            (cohortComparisonRes?.data?.practices || []).forEach((row) => {
                cohortComparisonMap[row.practice_id] = {
                    counts: row.counts,
                    rates: row.rates,
                    basis: cohortComparisonRes?.data?.basis || null,
                };
            });
            // Matrix status columns — transition basis, indexed by practice → treatment rows.
            const matrixBookedMap = indexByPractice(matrixBookedRes);
            const matrixAttendedMap = indexByPractice(matrixAttendedRes);
            const matrixStartedMap = indexByPractice(matrixStartedRes);
            const matrixClosedMap = indexByPractice(matrixClosedRes);

            const allPracticeData = practiceIds.map((practiceId, i) => {
                const [treatmentDetails, appointmentStats] =
                    perPracticeRes[i] || [];
                return {
                    practiceId,
                    totalLeads: totalLeadsMap[practiceId],
                    totalTreatments: totalTreatmentsMap[practiceId],
                    appointmentStats: appointmentStats?.data?.data || [],
                    treatmentDetails: treatmentDetails?.data?.results || [],
                    treatmentRevenueDist: revenueDistMap[practiceId] || [],
                    treatmentRevenueByNameDist: revenueByNameMap[practiceId] || [],
                    leadSourceStats: leadSourceMap[practiceId] || [],
                    leadReferralStats: leadReferralMap[practiceId] || [],
                    treatmentEnquiryCounts: enquiryMap[practiceId] || [],
                    archivedLeadCounts: archivedMap[practiceId] || [],
                    newEnquiryByTreatment: newEnquiryMap[practiceId] || [],
                    practiceComparison: cohortComparisonMap[practiceId] || null,
                    // Transition-basis status rows per treatment (Overview method).
                    matrixBookedByTreatment: matrixBookedMap[practiceId] || [],
                    matrixAttendedByTreatment: matrixAttendedMap[practiceId] || [],
                    matrixStartedByTreatment: matrixStartedMap[practiceId] || [],
                    matrixClosedByTreatment: matrixClosedMap[practiceId] || [],
                };
            });

            const validPracticeData = allPracticeData.filter(Boolean);

            // (a) Total Leads Summary Table
            // Count leads that ENTERED a given lead-stage within the period
            // (state-transition basis). Used by the Practice Comparison funnel so
            // Booked/Attended/Started share ONE basis — otherwise mixing
            // appointment-date Booked/Attended with transition-date Started
            // produced impossible conversion rates (Started > Attended > 100%).
            // Sum a per-state transition map (matrix source) across treatments to a
            // per-practice total. Practice Comparison funnel and the matrix now draw from
            // the SAME per-state state_transition_params source, so they reconcile on-page
            // (no more Practice Comparison ~45 vs matrix 30).
            const sumRows = (rows) => (rows || []).reduce((sum, obj) => sum + (obj?.count_id || 0), 0);

            const leadOverviewTable = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                totalLeads: p.totalLeads,
                totalTreatments: p.totalTreatments,
                // Appointment-date counts — feed the "Appointments Booked" /
                // "Consultations Attended" KPI cards (true appointment metrics,
                // consistent with the Booked/Attended list pages + the matrix).
                bookedAppointments: ['BOOKED', 'ATTENDED'].reduce((sum, status) => {
                    const match = p.appointmentStats.find((x) => x.status === status);
                    return sum + (match?.count_id || 0);
                }, 0),
                attendedConsultations: p.appointmentStats.find((x) => x.status === 'ATTENDED')?.count_id || 0,
                // Transition-basis funnel counts (per-state state_transition_params, the
                // Overview method) — feed the Practice Comparison conversion table. Same
                // source as the matrix status columns, so the two reconcile on the page.
                bookedFunnel: sumRows(p.matrixBookedByTreatment),
                attendedFunnel: sumRows(p.matrixAttendedByTreatment),
                startedTreatments: sumRows(p.matrixStartedByTreatment),
                practiceComparison: p.practiceComparison,
                notInterested: p.archivedLeadCounts.find((x) => x.is_archived)?.count_id || 0
            }));
            dispatch(setLeadOverviewTable(leadOverviewTable));

            // (b + e) Revenue by Treatment Category (Named) with color mapping
            let treatmentIdToName = {};
            let treatmentIdToColor = {};
            const revenueByCategory = validPracticeData.map((p) => {
                p.treatmentDetails.forEach((t) => {
                    const id = t.treatment_item?.treatment_category?.id;
                    const name = t.treatment_item?.treatment_category?.name;
                    const color = t.colour;
                    if (id && name) {
                        treatmentIdToName[id] = name;
                        if (color) {
                            treatmentIdToColor[id] = color;
                        }
                    }
                });

                const distribution = p.treatmentRevenueDist
                    .map((cat) => {
                        const categoryId = cat.lead_treatments__treatment__treatment_item__treatment_category;
                        const categoryName = treatmentIdToName[categoryId];
                        if (!categoryName) return null;
                        return {
                            categoryId,
                            categoryName,
                            revenue: cat.sum_lead_treatments__custom_price,
                            color: treatmentIdToColor[categoryId]
                        };
                    })
                    .filter(Boolean);

                return {
                    practiceId: p.practiceId,
                    distribution,
                    totalRevenue: distribution.reduce((acc, d) => acc + d.revenue, 0)
                };
            });
            dispatch(setRevenueByCategory(revenueByCategory));

            treatmentIdToName = {};
            treatmentIdToColor = {};
            const treatmentIdToMeta = {};
            const revenueByTreatmentName = validPracticeData.map((p) => {
                p.treatmentDetails.forEach((t) => {
                    const id = t?.id;
                    const name = t.treatment_item?.name;
                    const color = t.colour;
                    if (id && name) {
                        treatmentIdToName[id] = name;
                        treatmentIdToMeta[id] = { name, color };
                        if (color) {
                            treatmentIdToColor[id] = color;
                        }
                    }
                });

                const distribution = p.treatmentRevenueByNameDist
                    .map((cat) => {
                        const categoryId = cat.lead_treatments__treatment;
                        const categoryName = treatmentIdToName[categoryId];
                        if (!categoryName) return null;
                        return {
                            categoryId,
                            categoryName,
                            revenue: cat.sum_lead_treatments__custom_price,
                            color: treatmentIdToColor[categoryId] || "#FFF5CC"
                        };
                    })
                    .filter(Boolean);

                return {
                    practiceId: p.practiceId,
                    distribution,
                    totalRevenue: distribution.reduce((acc, d) => acc + d.revenue, 0)
                };
            })

            dispatch(setRevenueByTreatmentName(revenueByTreatmentName));

            // (f) Lead Source Distribution
            const leadSourceDist = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                sources: p.leadSourceStats
            }));
            dispatch(setLeadSourceDist(leadSourceDist));

            // (g) Lead Referral Distribution
            const referralDist = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                referrals: p.leadReferralStats
            }));
            dispatch(setReferralDist(referralDist));

            // (c) Treatment × Status Matrix with treatmentName and color.
            //
            // REPORTING CONTRACT (matches the Overview funnel — reporting-to-reporting
            // must reconcile). Every status column is TRANSITION basis via per-state
            // state_transition_params on /reports/leads/ — NOT appointment datetime and
            // NOT the date-only treatmentStateStats query (which over-counts). Supersedes
            // the mixed-basis approach (#3267) after Smile Rooms MK/June/Invisalign showed
            // Booked 3 (appointment) vs 30 (transition) — see the A1 contract decision.
            //   • New      → /leads/ created_at                        (New list page)
            //   • Booked   → transition into CONSULTATION_BOOKED       (Overview method)
            //   • Attended → transition into CONSULTATION_ATTENDED     (Overview method)
            //   • Started  → transition into TREATMENT_STARTED         (Overview method)
            //   • Closed   → transition into CLOSED                    (Overview method)
            // (By-Treatment uses a different endpoint (lead-treatment-statistics) and does
            //  NOT fully reconcile with Overview — tracked as its own reconciliation ticket;
            //  the matrix intentionally follows Overview, the multi-practice-capable source.)
            const buildMatrixEntry = (p, treatmentId, status, count, revenue) => {
                const meta = treatmentIdToMeta[treatmentId] || {};
                return {
                    practiceId: p.practiceId,
                    categoryId: treatmentId,
                    categoryName: meta.name || 'Unknown Treatment',
                    id: treatmentId,
                    color: meta.color,
                    status,
                    count: count || 0,
                    revenue: revenue || 0,
                };
            };
            const treatmentStatusMatrix = validPracticeData.flatMap((p) => {
                const entries = [];

                // New Enquiries — created_at basis
                (p.newEnquiryByTreatment || []).forEach((entry) => {
                    entries.push(buildMatrixEntry(
                        p, entry.lead_treatments__treatment, 'NEW',
                        entry.count_id, entry.sum_lead_treatments__custom_price,
                    ));
                });

                // Booked / Attended / Started / Closed — TRANSITION basis, one per-state
                // query each (Overview method). Revenue is realised-only (CLOSED); the
                // earlier stages carry 0 so the Revenue column stays consistent with the
                // rest of the app (revenue = closed treatments).
                const STAGE_SOURCES = [
                    ['CONSULTATION_BOOKED', p.matrixBookedByTreatment, false],
                    ['CONSULTATION_ATTENDED', p.matrixAttendedByTreatment, false],
                    ['TREATMENT_STARTED', p.matrixStartedByTreatment, false],
                    ['CLOSED', p.matrixClosedByTreatment, true],
                ];
                STAGE_SOURCES.forEach(([stage, rows, withRevenue]) => {
                    (rows || []).forEach((entry) => {
                        entries.push(buildMatrixEntry(
                            p, entry.lead_treatments__treatment, stage,
                            entry.count_id, withRevenue ? entry.sum_lead_treatments__custom_price : 0,
                        ));
                    });
                });

                return entries;
            });

            const groupedMap = new Map();

            treatmentStatusMatrix?.forEach(item => {
                const key = `${item.practiceId}__${item.categoryName}__${STATUS_SET[item.status] ?? "OTHERS"}`;

                if (!groupedMap.has(key)) {
                    groupedMap.set(key, {
                        practiceId: item.practiceId,
                        categoryName: item.categoryName,
                        categoryId: item.categoryId,
                        color: item.color,
                        status: STATUS_SET[item.status] ?? "OTHERS",
                        count: 0,
                        revenue: 0,
                        ids: []
                    });
                }

                const group = groupedMap.get(key);

                group.count += item.count;
                group.revenue += item.revenue;
                group.ids.push(item.id);

                if (!group.color && item?.color) {
                    group.color = item.color;
                }
                if (!group.categoryId && item?.categoryId) {
                    group.categoryId = item.categoryId;
                }
            });

            const result = Array.from(groupedMap.values());

            dispatch(setTreatmentStatusMatrix(result));

            // (h) Enquiry Count by Treatment Category (NEW status only)
            const treatmentEnquiryCount = validPracticeData.map((p) => ({
                practiceId: p.practiceId,
                enquiries: p.treatmentEnquiryCounts.map((item) => ({
                    categoryId: item.lead_treatments__treatment__treatment_item__treatment_category,
                    count: item.count_id
                }))
            }));
            dispatch(setTreatmentEnquiryCount(treatmentEnquiryCount));

            // (i) Per-practice monthly lead counts (created_at basis) for the Practice
            // Performance line view. group_by[created_at__month] returns the calendar
            // month number; sort ascending so the trend line reads left→right.
            const monthlyLeadsMap = indexByPractice(monthlyLeadsRes);
            const monthlyLeadsByPractice = practiceIds.map((pid) => ({
                practiceId: pid,
                months: (monthlyLeadsMap[pid] || [])
                    .map((r) => ({ month: r.created_at__month, count: r.count_id || 0 }))
                    .filter((m) => m.month != null)
                    .sort((a, b) => a.month - b.month),
            }));
            dispatch(setMonthlyLeadsByPractice(monthlyLeadsByPractice));

            dispatch(setAllPracticesFetchKey(`${practiceIds.sort().join(',')}|${dateRange.start}|${dateRange.end}`));
            dispatch(updateError({ message: 'Analytics loaded successfully', success: true }));
        } catch (error) {
            console.error('Failed to fetch analytics', error);
            dispatch(updateError({ message: error.message || 'Analytics load failed', success: false }));
        } finally {
            dispatch(setAllPracticesLoading(false));
        }
    };
}

// Reducer
export default slice.reducer;
